import { LearnLayoutContext } from "@-/dev.improve.haniverse.routes.nav.app.learn._context";
import { useRatingMutation } from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._lib.rating";
import { tinykeys } from "@-/com.npmjs.tinykeys";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import useLatest from "@-/com.npmjs.use-latest";
import { useEffect, type RefObject } from "react";
import { REVIEW_RATING_BY_DIGIT_KEY } from "#review-rating-by-digit-key";

function shouldIgnoreReviewCardKeyboardTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  if (target.isContentEditable) {
    return true;
  }
  const tagName = target.tagName;
  if (tagName === "TEXTAREA") {
    return true;
  }
  if (tagName !== "INPUT") {
    return false;
  }
  return !target.classList.contains("persistent-keyboard-input");
}

function shouldIgnoreReviewCardKeyboardEvent(event: KeyboardEvent): boolean {
  return event.repeat || event.isComposing || shouldIgnoreReviewCardKeyboardTarget(event.target);
}

export function useReviewOnKeyPress({ isFocusedRef }: { isFocusedRef: RefObject<boolean> }): void {
  const ratingMutation = useRatingMutation();
  const [presetRating, setAnswerInputState] = useContextSelectors(
    LearnLayoutContext,
    (c) => c.answerInputState.presetRating,
    (c) => c.setAnswerInputState,
  );
  const ratingMutationRef = useLatest(ratingMutation);
  const presetRatingRef = useLatest(presetRating);

  useEffect(() => {
    // Track the digit key currently held so keyup only submits if it matches
    // the most recent keydown — guards against stray keyups (window switches, etc.).
    let heldDigitKey: string | null = null;
    const unsubscribeKeyDown = tinykeys(
      globalThis.window,
      {
        "([1-4])": (event) => {
          if (!isFocusedRef.current) {
            return;
          }
          const rating = REVIEW_RATING_BY_DIGIT_KEY[event.key];
          if (rating === undefined) {
            return;
          }
          heldDigitKey = event.key;
          setAnswerInputState((state) => ({ ...state, presetRating: rating }));
          event.preventDefault();
        },
        Enter: (event) => {
          if (!isFocusedRef.current) {
            return;
          }
          const rating = presetRatingRef.current;
          if (rating !== null) {
            ratingMutationRef.current.mutate(rating);
          }
          event.preventDefault();
        },
      },
      { ignore: shouldIgnoreReviewCardKeyboardEvent },
    );
    const unsubscribeKeyUp = tinykeys(
      globalThis.window,
      {
        "([1-4])": (event) => {
          if (!isFocusedRef.current) {
            return;
          }
          const rating = REVIEW_RATING_BY_DIGIT_KEY[event.key];
          if (rating === undefined || event.key !== heldDigitKey) {
            return;
          }
          heldDigitKey = null;
          ratingMutationRef.current.mutate(rating);
          event.preventDefault();
        },
      },
      { event: "keyup", ignore: shouldIgnoreReviewCardKeyboardEvent },
    );
    return () => {
      unsubscribeKeyDown();
      unsubscribeKeyUp();
    };
  }, [isFocusedRef, presetRatingRef, ratingMutationRef, setAnswerInputState]);
}
